<template>
  <div ref="modalElemRef" class="modal" tabindex="-1" data-bs-backdrop="static" data-bs-keyboard="false" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered" :class="{ 'modal-sm': props.small, 'modal-lg': props.large }">
      <form class="modal-content" :class="{ ['needs-validation']: validate }" :nodvalidate="validate">
        <div class="modal-header">
          <component :is="props.titleLg ? 'h2' : 'h4'" class="modal-title">
            <slot name="icon"></slot>
            <slot v-if="$slots.title" name="title"></slot>
            <template v-else>{{ title }}</template>
          </component>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" :disabled="busyRef"></button>
        </div>
        <div class="modal-body">
          <slot :state="stateRef"></slot>
        </div>
        <div class="modal-footer" v-if="!props.hideActions">
          <div class="secondary-content">
            <slot name="secondaryContent"></slot>
            <button type="button" v-if="!$slots.secondaryContent && $slots.secondaryText" class="btn btn-inline btn-link" @click="emit('secondaryAction')">
              <slot name="secondaryText"></slot>
            </button>
          </div>
          <button type="submit" class="btn me-2" :class="okStyleClassRef" :disabled="!canSubmitRef" @click="onOk">
            <Spinner v-if="busyRef"/>
            <slot v-else name="okIcon"></slot>
            <span class="ok-text">
              <template v-if="busyRef && ($slots.busyText || props.busyText)">
                <slot v-if="$slots.busyText" name="busyText"></slot>
                <template v-else>{{ props.busyText }}</template>
              </template>
              <template v-else>
                <slot v-if="$slots.okText" name="okText"></slot>
                <template v-else>{{ okStateTextRef }}</template>
              </template>
            </span>
          </button>
          <button type="button" v-if="!props.info" class="btn btn-outline-primary" data-bs-dismiss="modal" :disabled="busyRef">
            <slot v-if="$slots.cancelText" name="cancelText"></slot>
            <template v-else>Cancel</template>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal-body {
  padding-top: 0;
}

.modal-footer {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;

  .secondary-content {
    margin-right: auto;
    font-size: 0.875rem;
  }

  .btn:not(.btn-link) {
    min-width: 100px;
  }

  button[type="submit"] {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.25rem;
    min-width: 80px;
  }
}
</style>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, onBeforeUnmount } from 'vue'
import { Modal as BsModal } from 'bootstrap'
import { hideModalAsync } from './AppUtil'
import Spinner from '@webapp/util/Spinner.vue'

type OkStyles = ("outline" | "warning" | "danger")

const props = defineProps<{
  title?: string,
  titleLg?: boolean,
  okText?: string,
  okStyle?: OkStyles,
  okDisabled?: boolean,
  okClose?: boolean,
  busyText?: string,
  info?: boolean,
  hideActions?: boolean,
  small?: boolean,
  large?: boolean,
  validate?: boolean
}>()

const emit = defineEmits<{
  (e: 'ok', state?: any, result?: string): void,
  (e: 'shown'): void,
  (e: 'hidden'): void,
  (e: 'secondaryAction'): void,
}>()

defineExpose({
  open,
  ok,
  reset,
  close 
})

const stateRef = ref<any>()
const busyRef = ref(false)

const modalElemRef = ref<InstanceType<typeof HTMLElement>>()

const okStyleClassRef = computed(() => 
  busyRef.value ? 'btn-secondary' : 
  props.okStyle == 'outline' ? 'btn-outline-primary' :
  props.okStyle ? `btn-${props.okStyle}` : 
  'btn-primary'
)
const okStateTextRef = computed(() => busyRef.value && props.busyText ? props.busyText : (props.okText ?? 'OK'))
const canSubmitRef = computed(() => !props.okDisabled && !busyRef.value)

onMounted(() => {
  document.addEventListener('keydown', onFormKey)
  modalElemRef.value?.addEventListener('shown.bs.modal', onShown)
  modalElemRef.value?.addEventListener('hidden.bs.modal', onHidden)
})

onBeforeUnmount(() => {
  close() // don't leave backdrop in place if reactivity unmounts an open modal
})

onUnmounted(() => {
  document.removeEventListener('keydown', onFormKey)
  modalElemRef.value?.removeEventListener('shown.bs.modal', onShown)
  modalElemRef.value?.removeEventListener('hidden.bs.modal', onHidden)
})

function onFormKey(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    close()
  }
}

async function onOk(e: Event) {
  e.preventDefault() // don't submit the page as a form

  if (props.info || props.okClose) {
    await close()
    emit('ok', stateRef.value) // do NOT set busy state in this case
  }
  else {
    ok()
  } 
}

function onShown(e: Event) {
  emit('shown')
}

function onHidden(e: Event) {
  emit('hidden')
}

function ok(result?: string) {
  busyRef.value = true
  emit('ok', stateRef.value, result)
}

function open(state?: any) {
  stateRef.value = state
  busyRef.value = false

  // HACK: Not sure why getInstance isn't sufficient here, but sometimes it returns null because apparently the BS Modal
  // hasn't been initialized yet. Calling getOrCreateInstance seems to fix it.
  BsModal.getOrCreateInstance(modalElemRef.value!).show()
}

function reset() {
  busyRef.value = false
}

async function close() {
  await hideModalAsync(modalElemRef.value!)
  busyRef.value = false
}
</script>
